/**
 * Host attrs for Android keyframe clip wipes (`wipeClipKeyframes.css.ts`).
 *
 * Keep `data-hub-clip-anim` for the whole active stop (not only pre-settle):
 * dropping it when `settled` flipped tore down `animation: … forwards` on the
 * same beat as `clip-path: none`, which read as a wipe-end flicker.
 *
 * Hold underlays keep the attr too — removing it on active→hold cancelled
 * every cell animation in one frame. Finished enters also keep
 * `data-hub-fill-lock` so active→hold cannot re-apply the enter animation.
 *
 * Enter: `data-hub-fill` (clip pin under the running animation), then
 * `data-hub-fill-lock` (`animation: none` near wipe-end). Depart:
 * `data-hub-depart-gone` pins the closed pose after close finishes.
 *
 * Depart still waits until `open` is false so settled→false cannot replay enter.
 */
export function wipeClipAnimAttrs(args: {
  animate: boolean;
  resume?: boolean;
  active: boolean;
  hold?: boolean;
  depart: boolean;
  open: boolean;
  fillPin?: boolean;
  fillLock?: boolean;
  departGone?: boolean;
}): {
  "data-hub-clip-anim": "" | undefined;
  "data-hub-hold": "" | undefined;
  "data-hub-fill": "" | undefined;
  "data-hub-fill-lock": "" | undefined;
  "data-hub-depart": "" | undefined;
  "data-hub-depart-gone": "" | undefined;
} {
  const {
    animate,
    resume = false,
    active,
    hold = false,
    depart,
    open,
    fillPin = false,
    fillLock = false,
    departGone = false,
  } = args;
  if (!animate || resume) {
    return {
      "data-hub-clip-anim": undefined,
      "data-hub-hold": undefined,
      "data-hub-fill": undefined,
      "data-hub-fill-lock": undefined,
      "data-hub-depart": undefined,
      "data-hub-depart-gone": undefined,
    };
  }
  // Enter + live stop: keyframe scope so forwards is not removed.
  if (active && !depart) {
    return {
      "data-hub-clip-anim": "",
      "data-hub-hold": undefined,
      // fill-lock already pins open — don't also emit fill.
      "data-hub-fill": fillPin && !fillLock ? "" : undefined,
      "data-hub-fill-lock": fillLock ? "" : undefined,
      "data-hub-depart": undefined,
      "data-hub-depart-gone": undefined,
    };
  }
  // Underlay during a later chapter — keep scope + finished enter lock.
  if (hold && !depart) {
    return {
      "data-hub-clip-anim": "",
      "data-hub-hold": "",
      "data-hub-fill": undefined,
      "data-hub-fill-lock": fillLock ? "" : undefined,
      "data-hub-depart": undefined,
      "data-hub-depart-gone": undefined,
    };
  }
  // Title return: depart+open pins the painted pose before close attaches.
  // Chapters never emit this; they close only after `open` is false.
  if (depart && open) {
    return {
      "data-hub-clip-anim": "",
      "data-hub-hold": undefined,
      "data-hub-fill": undefined,
      "data-hub-fill-lock": undefined,
      "data-hub-depart": "",
      "data-hub-depart-gone": undefined,
    };
  }
  if (depart && !open) {
    return {
      "data-hub-clip-anim": "",
      "data-hub-hold": undefined,
      "data-hub-fill": undefined,
      "data-hub-fill-lock": undefined,
      "data-hub-depart": "",
      "data-hub-depart-gone": departGone ? "" : undefined,
    };
  }
  return {
    "data-hub-clip-anim": undefined,
    "data-hub-hold": undefined,
    "data-hub-fill": undefined,
    "data-hub-fill-lock": undefined,
    "data-hub-depart": undefined,
    "data-hub-depart-gone": undefined,
  };
}
